import React, { useState, useEffect } from 'react';
import { Table, message, Typography, Space, Button, Spin, Tag, Input, Select, DatePicker } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import dayjs from 'dayjs';
import AdminLayout from '../Layout/AdminLayout';
import { SearchOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

const EmailLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [status, setStatus] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 100,
        total: 0
    });
    const navigate = useNavigate();

    // Check if user is admin
    const isAdmin = () => {
        const user = JSON.parse(localStorage.getItem('user'));
        return user?.roles?.[0]?.name === 'admin';
    };    

    const fetchLogs = async (page = 1, pageSize = 10, search = '', status = '', startDate = '', endDate = '') => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            
            if (!token) {
                throw new Error('No authentication token found');
            }

            const params = {
                page,
                per_page: pageSize,
                search,
                status
            };

            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const response = await axios.get(
                `${config.apiUrl}/api/v1/admin/email-logs`,
                {
                    params,
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                setLogs(response.data.data);
                setPagination({
                    current: response.data.meta.current_page,
                    pageSize: response.data.meta.per_page,
                    total: response.data.meta.total
                });
            }
        } catch (error) {
            console.error('Error fetching email logs:', error);
            if (error.response?.status === 401) {
                message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!');
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isAdmin()) {
            message.error('Bạn không có quyền truy cập trang này!');
            navigate('/');
            return;
        }
        const [startDate, endDate] = dateRange || [null, null];
        fetchLogs(
            1, 
            pagination.pageSize, 
            searchText, 
            status,
            startDate?.format('YYYY-MM-DD'),
            endDate?.format('YYYY-MM-DD')
        );
    }, [searchText, status, dateRange]);

    const handleSearch = (value) => {
        setSearchText(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleStatusChange = (value) => {
        setStatus(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const columns = [
        {
            title: 'Thời gian',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => dayjs(date)?.format('DD/MM/YYYY HH:mm:ss'),
            sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix()
        },
        {
            title: 'Thí sinh',
            dataIndex: ['user', 'name'],
            key: 'user_name',
            sorter: (a, b) => a?.user?.name?.localeCompare(b?.user?.name)
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: (a, b) => a?.email?.localeCompare(b?.email)
        },
        {
            title: 'Tiêu đề',
            dataIndex: 'subject',
            key: 'subject',
            sorter: (a, b) => a?.subject?.localeCompare(b?.subject)
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'success' ? 'green' : 'red'}>
                    {status === 'success' ? 'Thành công' : 'Thất bại'}
                </Tag>
            ),
            sorter: (a, b) => a?.status?.localeCompare(b?.status)
        },
        {
            title: 'Lỗi',
            dataIndex: 'error_message',
            key: 'error_message',
            render: (error) => error || '-'
        }
    ];

    const handleTableChange = (pagination) => {
        const [startDate, endDate] = dateRange || [null, null];
        fetchLogs(
            pagination.current, 
            pagination.pageSize, 
            searchText,
            status,
            startDate?.format('YYYY-MM-DD'),
            endDate?.format('YYYY-MM-DD')
        );
    };

    const emailLogsContent = (
        loading && logs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        ) : (
            <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
                    <Title level={3} style={{ margin: 0 }}>
                        Email Logs
                    </Title>
                    <Space size="middle">
                        <Select
                            placeholder="Trạng thái"
                            allowClear
                            style={{ width: 120 }}
                            onChange={handleStatusChange}
                            options={[
                                { value: 'success', label: 'Thành công' },
                                { value: 'failed', label: 'Thất bại' }
                            ]}
                        />
                        <RangePicker
                            onChange={handleDateRangeChange}
                            format="DD/MM/YYYY"
                        />
                        <Search
                            placeholder="Tìm kiếm theo email hoặc tiêu đề"
                            allowClear
                            enterButton={<SearchOutlined />}
                            size="large"
                            style={{ width: 300 }}
                            onSearch={handleSearch}
                        />
                    </Space>
                </div>
                
                <Table
                    size="small"
                    columns={columns}
                    dataSource={logs}
                    rowKey="id"
                    pagination={pagination}
                    loading={loading}
                    onChange={handleTableChange}
                    scroll={{ x: true }}
                />
            </>
        )
    );

    return (
        <AdminLayout title="Email Logs">
            {emailLogsContent}
        </AdminLayout>
    );
};

export default EmailLogs; 